import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useRevokeAccessControlApi } from 'apis/crm-access-control-management/use-revoke-access-control-api';
import React, { useCallback } from 'react';
import { FiTrash2, FiUser, FiUsers } from 'react-icons/fi';

interface RevokeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo: {
    id: number;
    name: string;
    email: string;
    phoneNumber?: string;
    role?: {
      id: number;
      name: string;
    };
    supplier?: {
      id: number;
      name: string;
    };
  };
}

export const RevokeAccessModal: React.FC<RevokeAccessModalProps> = ({ isOpen, onClose, userInfo }) => {
  // Self-contained mutation with success handling
  const { mutate: revokeAccess, isLoading: isRevoking } = useRevokeAccessControlApi({
    onSuccess: () => {
      onClose();
    },
  });

  // Memoized confirm handler
  const handleConfirm = useCallback(() => {
    revokeAccess(userInfo.id);
  }, [revokeAccess, userInfo.id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2} color='red.600'>
            <FiTrash2 />
            <Text>Revoke Access Control</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={5} align='stretch'>
            {/* Warning Alert */}
            <Alert status='warning' borderRadius='md'>
              <AlertIcon />
              <VStack align='start' spacing={1} flex='1'>
                <Text fontSize='sm' fontWeight='semibold'>
                  You are about to revoke access permissions
                </Text>
                <Text fontSize='xs' color='orange.700'>
                  This user will lose their brand access control permissions immediately.
                </Text>
              </VStack>
            </Alert>

            {/* Confirmation Message */}
            <Text>
              Are you sure you want to revoke access control permissions for{' '}
              <Text as='span' fontWeight='bold' color='red.600'>
                {userInfo.name}
              </Text>
              ?
            </Text>

            {/* User Information Card */}
            <VStack spacing={4} bg='gray.50' p={4} borderRadius='md' align='stretch'>
              <HStack spacing={2}>
                <FiUser size={16} color='#718096' />
                <Text fontSize='sm' fontWeight='semibold' color='gray.700'>
                  User Information
                </Text>
              </HStack>

              <VStack spacing={3} align='start' pl={6}>
                <HStack spacing={2}>
                  <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='20'>
                    Name:
                  </Text>
                  <Text fontSize='sm' color='gray.800'>
                    {userInfo.name}
                  </Text>
                </HStack>

                <HStack spacing={2}>
                  <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='20'>
                    Email:
                  </Text>
                  <Text fontSize='sm' color='gray.800'>
                    {userInfo.email}
                  </Text>
                </HStack>

                {userInfo.phoneNumber && (
                  <HStack spacing={2}>
                    <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='20'>
                      Phone:
                    </Text>
                    <Text fontSize='sm' color='gray.800'>
                      {userInfo.phoneNumber}
                    </Text>
                  </HStack>
                )}

                {userInfo.role && (
                  <HStack spacing={2}>
                    <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='20'>
                      Role:
                    </Text>
                    <Badge colorScheme='blue' size='sm'>
                      {userInfo.role.name}
                    </Badge>
                  </HStack>
                )}

                {userInfo.supplier && (
                  <HStack spacing={2}>
                    <Text fontSize='sm' fontWeight='medium' color='gray.600' minW='20'>
                      Supplier:
                    </Text>
                    <HStack spacing={1}>
                      <FiUsers size={14} color='#718096' />
                      <Text fontSize='sm' color='gray.800'>
                        {userInfo.supplier.name}
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </VStack>
            </VStack>

            {/* Final Warning */}
            <Alert status='error' borderRadius='md'>
              <AlertIcon />
              <VStack align='start' spacing={1} flex='1'>
                <Text fontSize='sm' fontWeight='semibold' color='red.800'>
                  This action cannot be undone
                </Text>
                <Text fontSize='xs' color='red.700'>
                  The user will need to be granted access again manually if required.
                </Text>
              </VStack>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onClose} isDisabled={isRevoking}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={handleConfirm}
              isLoading={isRevoking}
              loadingText='Revoking Access...'
              leftIcon={<FiTrash2 />}>
              Revoke Access
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
